/*
 * ===========================================================================================================
 * 🌐 checkConnection.ts
 * @description Verifica si el dispositivo tiene conexión real a internet mediante un ping a un servidor.
 *              No se basa solo en `navigator.onLine`, sino que intenta un fetch real para mayor fiabilidad.
 * ===========================================================================================================
 */

/**
 * 🔍 Verifica el estado real de la conexión a internet
 *
 * 💡 Usamos el endpoint `https://www.google.com/generate_204` porque:
 *    - Devuelve un código 204 (sin contenido) de forma rápida y ligera.
 *    - Está diseñado específicamente para pruebas de conectividad.
 *    - Es compatible con `mode: "no-cors"`, lo que evita errores CORS al no necesitar leer la respuesta.
 *
 * 🔐 `mode: "no-cors"` permite hacer la petición sin que el navegador bloquee el intento.
 * 📛 No podemos leer la respuesta ni el status, pero si la red está caída, lanzará un error.
 *
 * @returns {Promise<boolean>} - `true` si hay conexión activa, `false` si no la hay.
 */
export async function checkConnection(): Promise<boolean> {
  // 🔎 Si el navegador ya indica que no hay red, devolvemos directamente false
  if (!navigator.onLine) return false;

  try {
    // 📡 Intentamos hacer un "ping" a Google sin usar caché
    await fetch("https://www.google.com/generate_204", {
      method: "GET",
      cache: "no-store",
      mode: "no-cors", // 🔐 No esperamos respuesta legible, solo que no falle
    });

    // ✅ Si no lanza excepción, asumimos que hay red
    return true;
  } catch (e) {
    // ❌ Si ocurre un error (como timeout o DNS failure), no hay conexión
    return false;
  }
}
